import moment from 'moment/moment'
import { useEffect, useRef, useState } from 'react'

import { PSVisualizationDataType } from '../../../utils/visualization'
import { DEFAULT_CANVAS_HEIGHT, DEFAULT_CANVAS_WIDTH } from '../../../utils/visualization'
import { generateHeatMapRenderer } from './HeatMapRenderer'

export type HeatMapProps = {
    values: { [key: string]: number }[]
    labels: moment.Moment[]
    color: string
    dataType: PSVisualizationDataType
}

export const HeatMap = ({ values, labels, color, dataType }: HeatMapProps) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const heatMapManager = useRef(generateHeatMapRenderer())
    const [selectedElementIndex, setSelectedElementIndex] = useState<number | null>(null)
    const width = DEFAULT_CANVAS_WIDTH
    const height = DEFAULT_CANVAS_HEIGHT

    const handleMouseMove = (event: { clientX: number; clientY: number }) => {
        const canvas = canvasRef.current
        if (!canvas) return
        const rect = canvas.getBoundingClientRect()
        const x = event.clientX - rect.left
        const y = event.clientY - rect.top

        let elementFound = false
        heatMapManager.current.elementRepository.forEach((element, index) => {
            const elementX1 = element.mapRect.x
            const elementX2 = element.mapRect.x + element.mapRect.width
            const elementY1 = element.mapRect.y
            const elementY2 = element.mapRect.y + element.mapRect.height // Bottom of the canvas
            if (x >= elementX1 && x <= elementX2 && y >= elementY1 && y <= elementY2) {
                setSelectedElementIndex(index)
                elementFound = true
            }
        })

        if (!elementFound) {
            setSelectedElementIndex(null) // Reset if no element is hovered
        }
    }

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return
        const handleMouseOut = () => {
            setSelectedElementIndex(null)
        }
        canvas.addEventListener('mouseout', handleMouseOut)
        canvas.addEventListener('mousemove', handleMouseMove)

        return () => {
            canvas.removeEventListener('mouseout', handleMouseOut)
            canvas.removeEventListener('mousemove', handleMouseMove)
        }
    }, [])

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return
        const dpr = window.devicePixelRatio || 1
        canvas.width = width * dpr
        canvas.height = height * dpr
        canvas.style.width = width + 'px'
        canvas.style.height = height + 'px'
        const context = canvas.getContext('2d')
        if (!context) return
        context.scale(dpr, dpr)

        heatMapManager.current.drawValues(
            canvas,
            values,
            labels,
            color,
            dataType,
            selectedElementIndex,
            dpr
        )
    }, [values, labels, color, dataType, selectedElementIndex])

    return <canvas ref={canvasRef} />
}
